import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/firebase";
import Papa from "papaparse";
import md5 from "md5";

export const AVAILABLE_STORES = [
  'Carrefour',
  'Leclerc',
  'Intermarché',
  'Lidl',
  'Aldi',
  'Monoprix',
  'Franprix',
  'Casino',
  'Super U',
  'Biocoop',
  'Naturalia',
]

/**
 * Parse un CSV (sans écrire en DB)
 * → Retourne juste les lignes brutes
 */
export async function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      complete: (result) => {
        // conserver les lignes qui ont au moins une valeur non vide
        const rows = result.data.filter((r) =>
          Object.values(r || {}).some((v) => String(v ?? '').trim() !== '')
        );
        resolve(rows);
      },
      error: (err) => reject(err),
    });
  });
}

/**
 * Importe en DB dans BaseIngredients/all
 * en vérifiant les doublons (par nom)
 */
export async function importRowsToBaseIngredients(rows) {
  const docRef = doc(db, "BaseIngredients", "all");
  const snap = await getDoc(docRef);
  const existing = snap.exists() ? snap.data() : {};

  let toInsert = {};

  for (const row of rows) {
    const ingredientObj = mapCsvRowToBaseIngredient(row);
    const [id, ing] = Object.entries(ingredientObj)[0];

    // Doublon ? Vérif par nom
    const already = Object.values(existing).some(
      (e) => e.productName.toLowerCase().trim() === ing.productName.toLowerCase().trim()
    );

    if (!already) {
      toInsert[id] = ing;
    }
  }

  if (Object.keys(toInsert).length > 0) {
    await setDoc(docRef, toInsert, { merge: true });
  }

  return { added: Object.keys(toInsert).length };
}

/**
 * Transforme une ligne CSV en format Firestore pour BaseIngredients
 */
export function mapCsvRowToBaseIngredient(row) {
  const id = generateId(row["ingrédients"], row["marque"], row["type"]);

  return {
    [id]: {
      productName: row["ingrédients"] || "",
      productBrand: row["marque"] || "",
      category: row["type"] || "",
      servingQuantity: row["poids (g)"] || null,
      servingSize: row["poids (g)"] ? row["poids (g)"] + " g" : null,
      added_dt: new Date().toISOString(),
      novaGroup: { color: "green", string: "nova.1" },
      isEditingDisabled: true,
      isBaseIngredient: true,
      nutriments_map: [
        { key: "energy-kcal", value: row["kcal./100g"] ? row["kcal./100g"] + " kcal" : "" },
        { key: "fat", value: row["lipides"] ? row["lipides"] + " g" : "" },
        { key: "carbohydrates", value: row["glucides"] ? row["glucides"] + " g" : "" },
        { key: "proteins", value: row["protéines"] ? row["protéines"] + " g" : "" },
        { key: "fiber", value: row["fibres"] ? row["fibres"] + " g" : "" },
        { key: "salt", value: row["Sel"] ? row["Sel"] + " g" : "" },
        { key: "sugar", value: row["Sucre"] ? row["Sucre"] + " g" : "" },
        { key: "saturated-fat", value: row["AG saturés"] ? row["AG saturés"] + " g" : "" },
      ].filter((n) => n.value),
    },
  };
}

/**
 * Génère un ID déterministe basé sur nom + marque + type
 */
function generateId(productName, productBrand = "", category = "") {
  // include name + brand in a readable prefix and append an md5 hash
  const name = String(productName || "").trim();
  const brand = String(productBrand || "").trim();
  const raw = `${name}|${brand}|${category || ''}`;
  const hash = md5(raw);

  const sanitize = (s) =>
    String(s || "")
      // normalize accents (NFD) then remove diacritics so é -> e, ô -> o, etc.
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  // Use only the sanitized name as readable prefix, and append a short md5 suffix
  const prefix = sanitize(name) || hash.slice(0, 8);
  const shortHash = hash.slice(0, 8); // 8 chars is concise and unlikely to collide in this dataset
  return `${prefix}_${shortHash}`;
}

/**
 * Récupère les ingrédients de base (collection partagée)
 */
export async function getBaseIngredients() {
  const docRef = doc(db, "BaseIngredients", "all");
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return {};

  return docSnap.data();
}

/**
 * Supprime un ingrédient de base du document `all`.
 * Vérifie que le rôle utilisateur est admin.
 */
export async function deleteBaseIngredient(ingredientId, userRole) {
  if (!ingredientId) return;
  if (userRole !== "admin") {
    throw new Error("Unauthorized: admin role required");
  }

  const docRef = doc(db, "BaseIngredients", "all");
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const data = snap.data();
    if (!Object.prototype.hasOwnProperty.call(data, ingredientId)) return;
    delete data[ingredientId];
    await setDoc(docRef, data);
  } catch (err) {
    console.error("Erreur deleteBaseIngredient :", err);
    throw err;
  }
}

/**
 * Duplique un élément (ingrédient de base, etc.) dans la collection Ingredients/<userId>-ingredients.
 * - Utilise l'id fourni (barcode ou id) comme clé du map utilisateur
 * - Ne réécrit pas si l'entrée existe déjà
 * - Retourne { id, existed }
 */
export async function duplicateItemToUserIngredients(userId, item, itemId = null) {
  if (!userId) throw new Error('userId is required');
  if (!item) throw new Error('item is required');

  const ingredientId = itemId || item.id || item.barcode || null;
  if (!ingredientId) {
    throw new Error('ingredient id (barcode or unique id) is required');
  }

  const docRef = doc(db, 'Ingredients', `${userId}-ingredients`);

  try {
    const snap = await getDoc(docRef);
    const existing = snap.exists() ? snap.data() : {};

    if (Object.prototype.hasOwnProperty.call(existing, ingredientId)) {
      return { id: ingredientId, existed: true };
    }

    // copie défensive pour ne pas muter l'original
    const userItem = { ...item };

    // on ne stocke pas certains attributs de présentation / base
    if (Object.prototype.hasOwnProperty.call(userItem, 'id')) {
      delete userItem.id;
    }
    if (Object.prototype.hasOwnProperty.call(userItem, 'isBaseIngredient')) {
      delete userItem.isBaseIngredient;
    }
    
    if (Object.prototype.hasOwnProperty.call(userItem, 'isEditingDisabled')) {
      delete userItem.isEditingDisabled;
    }

    const payload = {
      ...existing,
      [ingredientId]: userItem,
    };

    await setDoc(docRef, payload, { merge: true });
    return { id: ingredientId, existed: false };
  } catch (err) {
    console.error('Erreur duplicateItemToUserIngredients:', err);
    throw err;
  }
}
